import { Subject, Observable } from '@reactivex/rxjs';
import { PostMessageDataSink } from '@graphistry/falcor-socket-datasource';

// TODO: These could be integrated into falcor
export const PATH_STRING_SELECTION_LABELS = ".selection.labels";
export const PATH_STRING_LABELS = ".labels";
const POSSIBLE_EXPORTS = new Set([PATH_STRING_SELECTION_LABELS, PATH_STRING_LABELS]);
const VIZ_SUBSCRIPTION_API_VERSION = 1;

const initPathMapOf = (value) => Array.from(POSSIBLE_EXPORTS.keys()).reduce((obj, k) => ({ ...obj, [k]: value }), {});

// Global State
// These break with HMR when coding the client-api locally
// Injecting these with  setupLegacyInterop is a mess
const subscribedPaths = initPathMapOf(false);
const pathOptions = initPathMapOf({}); // This is brittle, and relevant only as we solidify the API with blackbird

export function getSelectionLabelFragment(pointLength, edgeLength) {
    if (!subscribedPaths[PATH_STRING_SELECTION_LABELS] || (!pointLength &&  !edgeLength)) {
        return '';
    }

    const pageSize = pathOptions[PATH_STRING_SELECTION_LABELS]?.pageSize ?? 50;
    if (pathOptions[PATH_STRING_SELECTION_LABELS].withColumns) {
        return `labels: {
            point: { [0...${Math.min(pointLength, pageSize)}]: { index, title, globalIndex, label, columns } },
            edge: { [0...${Math.min(edgeLength, pageSize)}]: { index, title, globalIndex, value, columns } }
        },`;
    }
    return `labels: {
        point: { [0...${Math.min(pointLength, pageSize)}]: { index, title, globalIndex, label } },
        edge: { [0...${Math.min(edgeLength, pageSize)}]: { index, title, globalIndex, value } }
    },`;
}

export class LocalDataSink extends PostMessageDataSink {
    constructor(flags, getDataSource, ...args) {
        super(getDataSource, ...args);

        this.unsafeSelectAPI = flags?.flag_unsafe_jsapi_export_row;
        this.clientAPIVersion = undefined;
        this.availableExports = new Set(
            [PATH_STRING_LABELS].concat(this.unsafeSelectAPI ? [PATH_STRING_SELECTION_LABELS] : [])
        );

        // This is exposed to the react dom tree with onPublishedPathUpdated = publishedPathUpdatedSubject.next
        // Currently using the redux falcor library and containers to register when updates are needed.
        // TODO: Subscriptions could be generalized.
        this.publishedPathUpdatedSubject = new Subject();


        console.log(`Graphistry client: ready for API connection`);

        Observable.fromEvent(window, 'message')
            .filter(({ data }) => data?.type === 'ready' && data?.agent === 'graphistryjs')
            .do(({ data }) => { this.clientAPIVersion = data.subscriptionAPIVersion })
            .startWith({ source: parent }) // say hello first
            .mergeMapTo(
                getDataSource().get([
                    ['workbooks', 'open', 'id'],
                    ['workbooks', 'open', 'views', 'current', 'id']
                ]),
                ({ source }, cache) => ({ source, cache })
            )
            .subscribe(({ source, cache }) => {
                source?.postMessage?.(
                        {
                            cache,
                            type: 'init',
                            agent: 'graphistryjs',
                            version: process.env.APP_BUILD_TAG,
                            subscriptionAPIVersion: VIZ_SUBSCRIPTION_API_VERSION,
                        },
                        '*'
                    );
            });

            // If an ack isn't sent after the init, then the client is too old to support subscriptions.
            // We could check for that, and use the old method of pushing for lable updates.
            Observable.fromEvent(window, 'message')
                .filter(({ data }) => data && data.type === 'graphistry-init-ack')
                .do(({ data }) => { 
                    this.clientAPIVersion = data.subscriptionAPIVersion;
                })
                .subscribe();

        this.remoteSubscriptions = (Observable.fromEvent(window, 'message')
            .filter(({ data }) => data && data.agent === 'graphistryjs' && data.type && (data.type === 'graphistry-subscribe' ||  data.type === 'graphistry-unsubscribe'))
            .scan(({ counts }, { data }) => {
                const { path, type } = data;
                if (!this.availableExports.has(path)) {
                    console.error('Unavailable subscription path requested', 
                                  { data, allowedPaths: Array.from(this.availableExports).join(' '), flag_unsafe_jsapi_export_row: this.unsafeSelectAPI },
                                  '@LocalDataSink');
                    return { counts };
                }

                counts[path] = type === 'graphistry-subscribe' ? counts[path] + 1 : Math.max(0, counts[path] - 1);
                pathOptions[path] = data.options ?? {};

                return { counts };
            }, { counts: initPathMapOf(0) })
            .do(({ counts }) => {
                for (const [k, v] of Object.entries(counts)) {
                    subscribedPaths[k] = Boolean(v);
                }
            })
            .switchMap(({counts}) => this.publishedPathUpdatedSubject.filter(({ path }) => counts[path] > 0))
            .groupBy(({ path }) => path)
            .mergeMap((group) =>  group.debounceTime(50).do(({ path, data }) => {
                const message = { data, path, type: 'graphistry-sub-update', agent: 'graphistryjs' };
                console.debug(message, 'Sending update to iframe parent', '@localDataSink');
                parent.postMessage(message, '*');
            }))
            .subscribe(
                () => {},
                (err) => console.error('Error in remoteSubscriptions observable', err, '@LocalDataSink'),
                () => console.debug('remoteSubscriptions complete', '@LocalDataSink'),
            ));
    }

    unsubscribe() {
        this.remoteSubscriptions.unsubscribe();
        this.publishedPathUpdatedSubject.unsubscribe();
        super.unsubscribe();
    }
}
